import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { ProcedureType } from '@/types/cgt'

/** One time bucket. `counts` carries a slot for every requested type. */
export interface VolumeBucket {
  /** ISO day (UTC) the bucket starts on. */
  bucket: string
  counts: Record<string, number>
  total: number
}

export interface VolumeByType {
  type: ProcedureType
  total: number
  completed: number
  inProgress: number
  cancelled: number
  /** Share of the window's total, 0–100. */
  share: number
}

export interface VolumeDetail {
  id: string
  startedAt: string
  status: string
  type: ProcedureType | null
  templateName: string
  workOrderNumber: string | null
  patientMrn: string | null
}

export interface ProcedureVolumeReport {
  grain: 'daily' | 'weekly' | 'monthly'
  window: { from: string; to: string }
  buckets: VolumeBucket[]
  byType: VolumeByType[]
  total: number
  /** Runs whose template carries no type: in `total`, in no `byType` row. */
  untyped: number
  details: VolumeDetail[]
  /** Rows the window holds, which may exceed `details.length`. */
  detailTotal: number
  detailLimit: number
}

// ─── Report 1.2 — received vs completed ─────────────────────────────────────

export interface ThroughputBucket {
  bucket: string
  received: number
  completed: number
  /** received − completed: positive means the queue grew. */
  net: number
}

export interface ThroughputReport {
  grain: 'daily' | 'weekly' | 'monthly'
  window: { from: string; to: string }
  buckets: ThroughputBucket[]
  totalReceived: number
  totalCompleted: number
  net: number
  /** Complete, but with no date to place them on the chart. */
  completedUndated: number
}

// ─── Report 1.3 — on-time completion ────────────────────────────────────────

export type OnTimeVerdict = 'on_time' | 'late' | 'no_target'

export interface OnTimeRow {
  id: string
  type: ProcedureType | null
  completedAt: string
  dueDate: string | null
  verdict: OnTimeVerdict
  /** Days early (negative) or late (positive). Null without a target. */
  varianceDays: number | null
  templateName: string
  workOrderNumber: string | null
  patientMrn: string | null
}

export interface OnTimeByType {
  type: ProcedureType
  onTime: number
  late: number
  noTarget: number
  onTimePct: number | null
}

export interface OnTimeReport {
  window: { from: string; to: string }
  onTime: number
  late: number
  /** Completed with no due date — beside the percentage, never inside it. */
  noTarget: number
  onTimePct: number | null
  byType: OnTimeByType[]
  rows: OnTimeRow[]
  completedUndated: number
}

// ─── 1.4 Turnaround ─────────────────────────────────────────────────────────

/** A duration summary. Hours, so the client decides how to say it. */
export interface TurnaroundStats {
  count: number
  averageHours: number | null
  medianHours: number | null
  p90Hours: number | null
  minHours: number | null
  maxHours: number | null
}

export interface TurnaroundByType extends TurnaroundStats {
  type: string
}

export interface TurnaroundRow {
  id: string
  templateName: string
  type: string
  workOrderNumber: string | null
  patientMrn: string | null
  startedOn: string
  completedOn: string
  hours: number
}

export interface TurnaroundReport {
  window: { from: string; to: string }
  overall: TurnaroundStats
  byType: TurnaroundByType[]
  /** Finished in the window but untimable — beside the figures, never inside. */
  undatable: number
  rows: TurnaroundRow[]
}

// ─── 1.5 Backlog & aging ────────────────────────────────────────────────────

export interface AgingBucket {
  key: string
  label: string
  minDays: number
  maxDays: number | null
  count: number
}

export interface BacklogByType {
  type: string
  count: number
  oldestDays: number
}

export interface BacklogRow {
  id: string
  templateName: string
  type: string
  workOrderNumber: string | null
  patientMrn: string | null
  startedOn: string
  ageDays: number
}

export interface BacklogReport {
  window: { from: string; to: string }
  /** The day the snapshot describes — the window's exclusive end. */
  asOf: string
  total: number
  averageAgeDays: number | null
  medianAgeDays: number | null
  oldestDays: number | null
  buckets: AgingBucket[]
  byType: BacklogByType[]
  rows: BacklogRow[]
}

// ─── 2.1 Stock against minimum ──────────────────────────────────────────────

export type StockStatus = 'out' | 'below' | 'ok'

export interface StockRow {
  id: string
  materialId: string
  materialName: string
  materialType: string
  category: string
  onHand: number
  minStock: number | null
  reorderPoint: number | null
  status: StockStatus
  shortBy: number | null
  lots: number
}

export interface StockByCategory {
  category: string
  out: number
  below: number
  ok: number
}

export interface StockReport {
  /** The day the answer describes. Always today — see the endpoint. */
  asOf: string
  out: number
  below: number
  ok: number
  /** Holding stock with no minimum set — beside the counts, never inside. */
  noMinimum: number
  byCategory: StockByCategory[]
  rows: StockRow[]
}

// ─── 2.2 Consumption by procedure ───────────────────────────────────────────

export interface ConsumptionByProcedure {
  type: string
  qty: number
  materials: number
  runs: number
}

export interface ConsumptionByMaterial {
  materialId: string
  materialName: string
  category: string
  qty: number
  types: string[]
  runs: number
}

export interface ConsumptionReport {
  window: { from: string; to: string }
  totalQty: number
  /** Consumed by a step belonging to no run. Counted, never attributed. */
  unattributedQty: number
  byProcedure: ConsumptionByProcedure[]
  byMaterial: ConsumptionByMaterial[]
}

// ─── 2.3 Expiry risk ────────────────────────────────────────────────────────

export type ExpiryStatus = 'expired' | 'expiring'

export interface ExpiryRow {
  lotId: string
  lotNumber: string
  materialId: string
  materialName: string
  category: string
  quantity: number
  expiresOn: string
  /** Negative once it has passed. */
  daysToExpiry: number
  status: ExpiryStatus
}

export interface ExpiryByMaterial {
  materialId: string
  materialName: string
  category: string
  expiredQty: number
  expiringQty: number
  lots: number
  soonestExpiry: string
}

export interface ExpiryReport {
  window: { from: string; to: string }
  asOf: string
  /** Days AHEAD the report looked — the window's length, not its position. */
  horizonDays: number
  expiredLots: number
  expiredQty: number
  expiringLots: number
  expiringQty: number
  /** Lots holding stock with no expiry date. Beside the figures, never inside. */
  noExpiryLots: number
  byMaterial: ExpiryByMaterial[]
  rows: ExpiryRow[]
}

async function request<T>(path: string): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, { headers: authHeaders() })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API GET ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export const reportsApi = {
  /**
   * Report 1.1 — procedure volume by procedure type.
   *
   * `to` is EXCLUSIVE, matching the endpoint. Callers hold an inclusive window
   * and convert with `toQueryWindow` — the one place that conversion happens.
   */
  procedureVolume: (params: {
    from: string
    to: string
    grain: string
    types?: string[]
  }) => {
    const query = new URLSearchParams({
      from: params.from,
      to: params.to,
      grain: params.grain,
    })
    if (params.types && params.types.length > 0) query.set('types', params.types.join(','))
    return request<ProcedureVolumeReport>(`/reports/procedures/volume?${query.toString()}`)
  },

  /** Report 1.2 — received vs completed. Same window contract as above. */
  procedureThroughput: (params: { from: string; to: string; grain: string; types?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to, grain: params.grain })
    if (params.types?.length) query.set('types', params.types.join(','))
    return request<ThroughputReport>(`/reports/procedures/throughput?${query.toString()}`)
  },

  /** Report 1.3 — on-time completion. No grain: one figure over the window. */
  procedureOnTime: (params: { from: string; to: string; types?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.types?.length) query.set('types', params.types.join(','))
    return request<OnTimeReport>(`/reports/procedures/on-time?${query.toString()}`)
  },

  /**
   * Report 1.4 — turnaround. No grain: one summary over the window, and the
   * window bounds the COMPLETION instant rather than the start.
   */
  procedureTurnaround: (params: { from: string; to: string; types?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.types?.length) query.set('types', params.types.join(','))
    return request<TurnaroundReport>(`/reports/procedures/turnaround?${query.toString()}`)
  },

  /**
   * Report 1.5 — open backlog, as of the window's END.
   *
   * `from` is sent for a uniform window but bounds nothing: a run opened before
   * the window and still open is exactly what this reports.
   */
  procedureBacklog: (params: { from: string; to: string; types?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.types?.length) query.set('types', params.types.join(','))
    return request<BacklogReport>(`/reports/procedures/backlog?${query.toString()}`)
  },

  /**
   * Report 2.1 — stock against minimum. No window at all: lot quantities are
   * mutated in place, so there is no past level to ask for.
   */
  inventoryStock: (params: { categories?: string[] } = {}) => {
    const query = new URLSearchParams()
    if (params.categories?.length) query.set('categories', params.categories.join(','))
    const qs = query.toString()
    return request<StockReport>(`/reports/inventory/stock${qs ? `?${qs}` : ''}`)
  },

  /**
   * The tenant's material categories, for the category filter.
   *
   * Its own call rather than a field on each report: the filter has to be
   * usable before a report has rendered, or it cannot shape the first request.
   */
  materialCategories: () => request<{ categories: string[] }>('/reports/inventory/categories'),

  /** Report 2.3 — expiry risk. The window's LENGTH is a forward horizon. */
  inventoryExpiry: (params: { from: string; to: string; categories?: string[] }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.categories?.length) query.set('categories', params.categories.join(','))
    return request<ExpiryReport>(`/reports/inventory/expiry?${query.toString()}`)
  },

  /** Report 2.2 — consumption by procedure, over the window. */
  inventoryConsumption: (params: {
    from: string
    to: string
    types?: string[]
    categories?: string[]
  }) => {
    const query = new URLSearchParams({ from: params.from, to: params.to })
    if (params.types?.length) query.set('types', params.types.join(','))
    if (params.categories?.length) query.set('categories', params.categories.join(','))
    return request<ConsumptionReport>(`/reports/inventory/consumption?${query.toString()}`)
  },
}
